import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useMediaFetch } from '../hooks/useMediaFetch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SearchHeader from '../components/SearchHeader';
import MediaGrid from '../components/MediaGrid';
import { MediaFile } from '../components/MediaGrid';
import type { ImageGenerationContext } from '~/data/imageGenerationPolling';

const Container = styled.div`
  h3 {
    font-family: ralewayBold;
    font-weight: unset !important;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    padding-bottom: 10px;
  }
`;

const GenerationError = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #e8cd97;
  border-radius: 6px;
  background: #fff8ed;
  color: #713f12;
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
`;

type AiSectionProps = {
  onSelect: (file: MediaFile) => void;
  context?: ImageGenerationContext;
};

const AiSection: React.FC<AiSectionProps> = ({ onSelect, context }) => {
  const [search, setSearch] = useState('');
  const [style, setStyle] = useState('natural');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generatedFile, setGeneratedFile] = useState<MediaFile | null>(null);
  const { apiService } = useEditorStore();
  const { files, loading, fetchMedia, deleteMedia, setFiles } = useMediaFetch(
    'image',
    'dalle'
  );
  const isRequestInProgress = useRef(false);
  const generationController = useRef<AbortController | null>(null);

  useEffect(() => () => generationController.current?.abort(), []);

  const loadMore = async () => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true;
    try {
      const hasMore = await fetchMedia(page);
      setCanLoadMore(hasMore ?? false);
    } finally {
      isRequestInProgress.current = false;
    }
  };

  const {
    page,
    loadMoreRef,
    setCanLoadMore,
    isLoading: isInfiniteLoading
  } = useInfiniteScroll(loadMore);

  const handleGenerate = async () => {
    if (!apiService || !search.trim() || generationController.current) return;

    const controller = new AbortController();
    generationController.current = controller;
    setIsGenerating(true);
    setGenerationError(null);
    try {
      const response = await apiService.generateImageWithAI(
        search,
        style,
        context,
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (response && 'picture' in response && response.picture) {
        setGeneratedFile(response.picture as MediaFile);
      } else {
        setGenerationError(
          response &&
            'message' in response &&
            typeof response.message === 'string'
            ? response.message
            : 'Image generation could not be completed. Please try again.'
        );
      }
    } catch {
      if (controller.signal.aborted) return;
      setGenerationError(
        'Image generation could not be completed. Please try again.'
      );
    } finally {
      if (!controller.signal.aborted) setIsGenerating(false);
      if (generationController.current === controller)
        generationController.current = null;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteMedia(id);
      if (response.success) {
        setFiles(prevFiles => prevFiles.filter(file => file.id !== id));
        if (generatedFile?.id === id) {
          setGeneratedFile(null);
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const displayFiles =
    isGenerating || generatedFile
      ? generatedFile
        ? [generatedFile]
        : []
      : files;

  return (
    <Container>
      <h3>Generate image with AI</h3>
      <SearchHeader
        variant="ai"
        placeholder="Generate with AI"
        value={search}
        onChange={setSearch}
        onSearch={handleGenerate}
        loading={isGenerating}
        loadingText="Generating..."
        style={style}
        onStyleChange={setStyle}
      />
      {generationError && (
        <GenerationError role="alert">{generationError}</GenerationError>
      )}
      {!isGenerating && !generatedFile && files && files.length > 0 && (
        <h3>Recently Generated</h3>
      )}
      {generatedFile && <h3>Generated Image</h3>}
      <MediaGrid
        files={displayFiles}
        loading={loading || isInfiniteLoading || isGenerating}
        type="image"
        onSelect={onSelect}
        onDelete={handleDelete}
        loadMoreRef={!isGenerating && !generatedFile ? loadMoreRef : undefined}
        showNoResults={!generationError}
      />
    </Container>
  );
};

export default AiSection;
